import { computed } from 'vue';
import { useLoyalty } from '~/composables/loyalty/useLoyalty';
import { useProductPrice } from '~/modules/price/composables/useProductPrice';
import { useBrand } from '~/composables/useBrand';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import { usePpc } from '~/modules/ppc/composables/usePPC';

export default function usePromotionPerks() {
  const selectedPrice = useProductPrice();
  const { isBcom } = useBrand();
  const { isUserNonLoyalist } = useLoyalty();
  const { isPpcEnabled } = useFeatureEligibility();
  const { ppcIconName, isPowerPointsBelowPointsThreshold } = usePpc();

  const isGWP = (offer = {}) => (isBcom ? offer.isGWP : offer.giftProducts?.length > 0);

  const getMainOffers = (badges) =>
    badges.reduce(
      (mainOffers, offer) => {
        if (isBcom && offer.isLoyallistOffer && (!isPpcEnabled || isUserNonLoyalist.value || isPowerPointsBelowPointsThreshold.value)) {
          mainOffers.loyallistOffers.push(offer);
        } else if (isGWP(offer)) {
          mainOffers.giftOffers.push(offer);
        } else if (!offer.isLoyallistOffer) {
          mainOffers.otherOffers.push(offer);
        }

        return mainOffers;
      },
      {
        giftOffers: [],
        otherOffers: [],
        loyallistOffers: [],
      },
    );

  const createPromotion = (offer = {}, isMultipleOffers) => {
    const promotion = {
      mainOffer: isBcom ? offer.description || offer.checkoutDescription : offer.header,
      moreDetails: isMultipleOffers ? 'See more offers' : 'See offer details',
    };
    return isGWP(offer)
      ? Object.assign(promotion, { icon: isBcom ? 'gift-wrap-dark-medium' : 'registry-dark-medium', type: 'gwp' })
      : Object.assign(promotion, { icon: 'personalized-offers-dark-medium', type: 'other' });
  };

  const createLoyallistPromotion = (offer = {}) => ({
    mainOffer: offer.offerName,
    icon: ppcIconName.value || 'loyallist-small',
    type: 'loyalty',
  });

  const offers = computed(() => {
    const badgesList = selectedPrice?.value?.perksPromotions || [];
    const { otherOffers, giftOffers, loyallistOffers } = getMainOffers(badgesList);

    return {
      giftOffer: createPromotion(giftOffers[0], giftOffers.length > 1),
      otherOffer: createPromotion(otherOffers[0], otherOffers.length > 1),
      loyallistOffer: isBcom ? createLoyallistPromotion(loyallistOffers[0]) : {},
    };
  });

  return {
    offers,
  };
}
